import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Container, Text, Sprite, Graphics } from "@pixi/react";
import * as PIXI from "pixi.js";
import { TextStyle } from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import anime from "animejs";
import {
  closeLeaveGamePopup,
  setUserInterfaceDisabled,
} from "../../../../State/actions";
import PopupButton from "../Buttons/PopupButton";
import { selectLeaveGamePopupActive } from "../../../../State/selectors";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { getUITexture } from "../../../../assets";
import { FONT_CONFIG } from "../../../../pixiSettings";
import { PixiSettings } from "../../../../pixiSettings";


// Overlay settings
const OVERLAY_BACKGROUND_ALPHA = 0.6;
const OVERLAY_BACKGROUND_COLOR = 0x090f41;
const OVERLAY_FADE_DURATION = 200;

// Popup animation settings
const POPUP_ANIM_DURATION = 250;

// Popup configuration
const POPUP_CONFIG = {
  basePadding: 40,
  baseMessageFontSize: 24,
  baseButtonFontSize: 24,
  baseTextButtonGap: 40,
  baseContentWidth: 480,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.55 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.6 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.75 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.75 },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 1.0 },
    },
  },
};

const LeaveGamePopup = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dimensions, device, orientation } = useDimensions();
  const isOpen = useSelector(selectLeaveGamePopupActive);

  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const popupContentRef = useRef(null);



  const message = useMemo(() => t("leave_game_message", { ns: "common" }), [t]);

  const deviceConfigMemo = useMemo(() => {
    return (
      POPUP_CONFIG.DEVICE_CONFIGS[device]?.[orientation] ||
      POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP][
        PixiSettings.ORIENTATION.LANDSCAPE
      ]
    );
  }, [device, orientation]);

  const centerX = useMemo(() => dimensions.width / 2, [dimensions.width]);
  const centerY = useMemo(() => dimensions.height / 2, [dimensions.height]);

  const frameTexture = useMemo(
    () => getUITexture("popup_frame_horizontal"),
    [],
  );

  const popupScale = useMemo(() => {
    const deviceScale = deviceConfigMemo.scaleMultiplier || 1.0;
    if (!frameTexture) return deviceScale;

    const idealContentWidth = POPUP_CONFIG.baseContentWidth * deviceScale;
    const idealPadding = POPUP_CONFIG.basePadding * deviceScale;
    const idealPanelWidth = idealContentWidth + idealPadding * 2;
    const scaleX = idealPanelWidth / frameTexture.width;
    const idealFrameWidth = frameTexture.width * scaleX;
    const idealFrameHeight = frameTexture.height * scaleX;

    const maxWidth = dimensions.width * 0.8;
    const maxHeight = dimensions.height * 0.8;
    const widthRatio = idealFrameWidth > maxWidth ? maxWidth / idealFrameWidth : 1;
    const heightRatio = idealFrameHeight > maxHeight ? maxHeight / idealFrameHeight : 1;
    const clampRatio = Math.min(widthRatio, heightRatio);

    return deviceScale * clampRatio;
  }, [deviceConfigMemo, frameTexture, dimensions.width, dimensions.height]);

  const padding = useMemo(
    () => POPUP_CONFIG.basePadding * popupScale,
    [popupScale],
  );
  const messageFontSize = useMemo(
    () => Math.round(POPUP_CONFIG.baseMessageFontSize * popupScale),
    [popupScale],
  );
  const textButtonGap = useMemo(
    () => POPUP_CONFIG.baseTextButtonGap * popupScale,
    [popupScale],
  );
  const contentWidth = useMemo(
    () => POPUP_CONFIG.baseContentWidth * popupScale,
    [popupScale],
  );

  const messageStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: messageFontSize,
        fontWeight: "700",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentWidth,
      }),
    [messageFontSize, contentWidth],
  );



  // Button sized proportionally to content width
  const buttonWidth = useMemo(() => contentWidth * 0.25, [contentWidth]);
  const buttonHeight = useMemo(() => {
    const buttonTexture = getUITexture("popup_button");
    if (!buttonTexture) return buttonWidth * 0.5;
    const aspect = buttonTexture.height / buttonTexture.width;
    return buttonWidth * aspect;
  }, [buttonWidth]);

  // Frame scaling
  const textMetrics = useMemo(
    () => PIXI.TextMetrics.measureText(message, messageStyle),
    [message, messageStyle],
  );
  const textHeight = useMemo(() => textMetrics.height, [textMetrics.height]);

  const desiredPanelWidth = useMemo(
    () => contentWidth + padding * 2,
    [contentWidth, padding],
  );
  const desiredPanelHeight = useMemo(
    () => padding + textHeight + textButtonGap + buttonHeight + padding,
    [textHeight, textButtonGap, buttonHeight, padding],
  );

  const frameScale = useMemo(() => {
    if (!frameTexture) return 1;
    const scaleX = desiredPanelWidth / frameTexture.width;
    const scaleY = desiredPanelHeight / frameTexture.height;
    return Math.max(scaleX, scaleY);
  }, [frameTexture, desiredPanelWidth, desiredPanelHeight]);

  const frameSpriteWidth = useMemo(
    () => (frameTexture ? frameTexture.width * frameScale : 0),
    [frameTexture, frameScale],
  );
  const frameSpriteHeight = useMemo(
    () => (frameTexture ? frameTexture.height * frameScale : 0),
    [frameTexture, frameScale],
  );

  // Positions
  const buttonYPos = useMemo(
    () => frameSpriteHeight / 2 - padding - buttonHeight / 2,
    [frameSpriteHeight, padding, buttonHeight],
  );

  const textAreaTop = useMemo(
    () => -frameSpriteHeight / 2 + padding,
    [frameSpriteHeight, padding],
  );
  const textAreaBottom = useMemo(
    () => buttonYPos - textButtonGap,
    [buttonYPos, textButtonGap],
  );
  const messageY = useMemo(
    () => (textAreaTop + textAreaBottom) / 2,
    [textAreaTop, textAreaBottom],
  );

  const yesText = useMemo(() => t("yes", { ns: "common" }), [t]);
  const noText = useMemo(() => t("no", { ns: "common" }), [t]);

  const yesButtonX = useMemo(
    () => -buttonWidth / 2 - padding * 0.5,
    [buttonWidth, padding],
  );
  const noButtonX = useMemo(
    () => buttonWidth / 2 + padding * 0.5,
    [buttonWidth, padding],
  );

  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_BACKGROUND_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
  }, [isClosing]);

  const handleYes = useCallback(() => {
    if (isClosing) return;
    window.location.reload();
  }, [isClosing]);

  // Open animation
  useEffect(() => {
    if (isOpen && !isClosing) {
      setIsVisible(true);
      dispatch(setUserInterfaceDisabled(true));
      anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          if (popupContentRef.current) {
            popupContentRef.current.alpha = 0;
            popupContentRef.current.scale.set(0.85);
            anime({
              targets: popupContentRef.current,
              alpha: 1,
              duration: POPUP_ANIM_DURATION,
              easing: "easeOutQuad",
              complete: () => {
                setHasAnimated(true);
              },
            });
            anime({
              targets: popupContentRef.current.scale,
              x: 1,
              y: 1,
              duration: POPUP_ANIM_DURATION,
              easing: "easeOutQuad",
            });
          }
        },
      });
    } else if (!isOpen && !isClosing) {
      dispatch(setUserInterfaceDisabled(false));
      setIsVisible(false);
      setOverlayAlpha(0);
      setHasAnimated(false);
    }
  }, [isOpen, isClosing, dispatch]);

  // Close animation
  useEffect(() => {
    if (!isClosing) return;
    let overlayAnim = null;
    let popupAnim = null;
    if (popupContentRef.current) {
      popupAnim = anime({
        targets: popupContentRef.current,
        alpha: 0,
        duration: POPUP_ANIM_DURATION,
        easing: "easeInQuad",
      });
      anime({
        targets: popupContentRef.current.scale,
        x: 0.85,
        y: 0.85,
        duration: POPUP_ANIM_DURATION,
        easing: "easeInQuad",
      });
    }
    overlayAnim = anime({
      targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_DURATION,
      delay: POPUP_ANIM_DURATION,
      easing: "easeInQuad",
      update: (anim) => {
        setOverlayAlpha(anim.animations[0].currentValue);
      },
      complete: () => {
        setOverlayAlpha(0);
        dispatch(setUserInterfaceDisabled(false));
        dispatch(closeLeaveGamePopup());
        setIsClosing(false);
        setIsVisible(false);
        setHasAnimated(false);
      },
    });
    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (popupAnim) popupAnim.pause();
    };
  }, [isClosing, dispatch]);

  if (!isVisible && !isOpen) {
    return null;
  }

  return (
    <Container name="leave_game_popup" zIndex={10000} sortableChildren={true}>
      <Graphics
        draw={drawOverlay}
        alpha={overlayAlpha}
        eventMode="static"
        pointerdown={(e) => {
          e.stopPropagation();
        }}
        zIndex={10000}
      />

      <Container
        name="popup_content"
        ref={popupContentRef}
        zIndex={10001}
        x={centerX}
        y={centerY}
        alpha={hasAnimated ? 1 : 0}
        scale={hasAnimated ? 1 : 0.85}
      >
        {/* Frame background */}
        {frameTexture && (
          <Sprite
            texture={frameTexture}
            anchor={0.5}
            width={frameSpriteWidth}
            height={frameSpriteHeight}
          />
        )}

        {/* Message */}
        <Text
          text={message}
          style={messageStyle}
          anchor={0.5}
          x={0}
          y={messageY}
          resolution={2}
        />

        {/* No button (left) */}
        <PopupButton
          x={noButtonX}
          y={buttonYPos}
          text={noText}
          onClick={handleClose}
          width={buttonWidth}
          height={buttonHeight}
          accessibleTitle={noText}
        />

        {/* Yes button (right) */}
        <PopupButton
          x={yesButtonX}
          y={buttonYPos}
          text={yesText}
          onClick={handleYes}
          width={buttonWidth}
          height={buttonHeight}
          accessibleTitle={yesText}
        />
      </Container>
    </Container>
  );
};

export default LeaveGamePopup;
